'use client';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { nextjsPlugin } from '@datadog/browser-rum-nextjs';
import { hasDatadogConsent } from '@/lib/datadog/tracking-consent';
import { registerLandingPageReadyRum } from '@/lib/datadog/landing-page-ready';
import { registerRumApi } from '@/lib/datadog/rum';
import { redactDiagnosticUrls } from '@/lib/datadog/redact';
import {
    shouldDropDatadogLogsEvent,
    shouldDropDatadogRumEvent,
} from '@workspace/analytics/filters/marketing-tracking-noise';

export { onRouterTransitionStart } from '@datadog/browser-rum-nextjs';

export function initializeDatadog(resolveArea: (pathname: string) => string): void {
    const applicationId = process.env.NEXT_PUBLIC_DATADOG_APP_ID;
    const clientToken = process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN;
    if (process.env.NEXT_PUBLIC_DEPLOY_ENV !== 'prod' || !applicationId || !clientToken) return;

    const sharedConfiguration = {
        clientToken,
        site: 'ap1.datadoghq.com',
        service: 'enter-web',
        env: 'production',
        version: process.env.NEXT_PUBLIC_APP_VERSION,
        sessionSampleRate: 100,
        trackingConsent: 'not-granted',
    } as const;

    try {
        datadogRum.init({
            ...sharedConfiguration,
            applicationId,
            sessionReplaySampleRate: 20,
            trackResources: true,
            trackUserInteractions: true,
            trackLongTasks: true,
            defaultPrivacyLevel: 'mask-user-input',
            beforeSend: event => {
                if (!hasDatadogConsent() || shouldDropDatadogRumEvent(event)) return false;
                if (event.view?.url) {
                    try {
                        event.context = { ...event.context, app_area: resolveArea(new URL(event.view.url).pathname) };
                    } catch { /* 无法解析的地址由统一脱敏逻辑丢弃。 */ }
                }
                redactDiagnosticUrls(event);
                return true;
            },
            plugins: [nextjsPlugin()],
        });
        registerRumApi(datadogRum);
        registerLandingPageReadyRum(datadogRum);
    } catch (error) {
        console.warn('[datadog-rum] initialization failed', error);
    }

    try {
        datadogLogs.init({
            ...sharedConfiguration,
            forwardErrorsToLogs: true,
            forwardConsoleLogs: ['error'],
            beforeSend: event => {
                if (!hasDatadogConsent() || shouldDropDatadogLogsEvent(event)) return false;
                redactDiagnosticUrls(event);
                return true;
            },
        });
    } catch (error) {
        console.warn('[datadog-logs] initialization failed', error);
    }
}

export function setDatadogSdkIdentity(user: { id: string; [key: string]: unknown }): void {
    for (const sdk of [datadogRum, datadogLogs]) {
        try { sdk.setUser(user); }
        catch { console.warn('[datadog] identity update failed'); }
    }
}

export function resetDatadogSdkIdentity(): void {
    for (const sdk of [datadogRum, datadogLogs]) {
        try { sdk.clearUser(); }
        catch { console.warn('[datadog] user reset failed'); }
    }
}

export function applyDatadogTrackingConsent(granted: boolean): void {
    for (const [name, sdk] of [['rum', datadogRum], ['logs', datadogLogs]] as const) {
        try { sdk.setTrackingConsent(granted ? 'granted' : 'not-granted'); }
        catch { console.warn(`[datadog-${name}] consent sync failed`); }
        if (!granted) {
            try { sdk.clearUser(); }
            catch { console.warn(`[datadog-${name}] user reset failed`); }
        }
    }
}

export function writeDatadogLog(
    level: 'debug' | 'info' | 'warn' | 'error',
    message: string,
    context?: Record<string, unknown>,
): void {
    try { datadogLogs.logger.log(message, context, level); }
    catch { console.warn('[datadog] log failed'); }
}
